// @flow

import React, { useCallback } from 'react';
import bemify, { toModifiers } from 'bemify-js';
import { get } from 'lodash';

import { Icon } from '@zapier/common-components';

import LoadingStar from 'web-ssr/app/common/components/LoadingStar';
import SearchBox from 'web-ssr/app/common/components/SearchBox';
import ServiceLogo from 'web-ssr/app/common/components/ServiceLogo';

import ServicesSearchDropdown from './ServicesSearchDropdown';
import './ServicesSearch.scss';

import type { KindType } from 'web-ssr/app/common/types';
import type { publicAppSearchQuery_getAppsByNameContains_edges } from 'web-ssr/graphql/types';

const bem = bemify('services-search');

type Props = {
  ariaLabel?: string,
  className?: string,
  isDropdownOpen: boolean,
  isLoading: boolean,
  onChange: (value: string) => void,
  onClear?: () => void,
  onFocus: () => void,
  onFocusOut?: () => void,
  onSelectService: (service: {}) => void,
  placeholder?: string,
  selectedService: ?{},
  apps: Array<publicAppSearchQuery_getAppsByNameContains_edges>,
  value: string,
  skipQuery: boolean,
  kind?: KindType,
  inputRef?: any,
};

const ServicesSearch = (props: Props) => {
  const {
    ariaLabel,
    className,
    isDropdownOpen,
    isLoading,
    onChange,
    onClear,
    onFocus,
    onFocusOut,
    onSelectService,
    placeholder,
    selectedService,
    apps,
    value,
    skipQuery,
    kind,
    inputRef,
  } = props;

  // Call the `onFocusOut` prop function if the next element to receive
  // focus is not a child of div that contains the search form and search results.
  // This allows the search form to be closed if the user navigates
  // away from the search via keyboard navigation.
  //
  // React does not implement the `focusout` event, so an event listener must be
  // added the old-fashioned way. Wrap the `addEventListener` call in a `useCallback()`
  // hook to prevent additional listeners from being attached.
  const containerRef = useCallback(div => {
    if (!div) {
      return;
    }
    div.addEventListener('focusout', () => {
      // Without the setTimeout, `document.activeElement` will not return the expected value.
      // This is because `focusout` fires before `focus`, so the next element will not yet have received focus.
      setTimeout(() => {
        if (
          typeof onFocusOut === 'function' &&
          !div.contains(document.activeElement)
        ) {
          onFocusOut();
        }
      }, 0);
    });
    // The empty array prevents multiple event listeners from being added,
    // because `containerRef()` is only called when the element is attached to the DOM.
    // The downside is that the event listener is not updated if `onFocusOut` changes.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <div className={bem(className, toModifiers(kind))} ref={containerRef}>
      <SearchBox
        ariaLabel={ariaLabel}
        inputRef={inputRef}
        isLoading={isLoading}
        kind={kind}
        name="search"
        onChange={onChange}
        onClear={onClear}
        onFocus={onFocus}
        placeholder={placeholder}
        renderIcon={iconProps => {
          if (isLoading) {
            return <LoadingStar className={iconProps.className} />;
          }

          if (selectedService) {
            const serviceName = get(selectedService, ['name']);
            const serviceImages = get(selectedService, ['images'], {});

            return (
              <ServiceLogo
                className={iconProps.className}
                images={serviceImages}
                serviceName={serviceName}
              />
            );
          }

          return (
            <Icon
              className={bem(iconProps.className)}
              name="search"
              size={18}
            />
          );
        }}
        showClearButton={true}
        type="text"
        value={value}
      />
      <div aria-live="polite">
        <ServicesSearchDropdown
          apps={apps}
          isDropdownOpen={
            // The dropdown should not appear when the search value is empty or
            // the search box is making an async query
            !isLoading && !skipQuery && isDropdownOpen
          }
          onSelectService={onSelectService}
        />
      </div>
    </div>
  );
};

export default ServicesSearch;
